// liveStream.js
'use strict';
import { fromJS } from 'immutable';
import {
  MERGE_LIVE_STREAM_DATA,
  MERGE_LIVE_STREAMS_DATA,
  REMOVE_LIVE_STREAM_DATA,
  SET_LIVE_STREAM_DATA,
  // -- PLOP_PREPEND_REDUCER_ACTION_TYPE --
  LOGOUT,
} from '../ActionTypes.js';

export const defaultState = fromJS({});

/*
livestream: {
  [id]: Livestream
}

Livestream: {
  id: String,
  sessionId: String,
  title: String,
  tags: Array<String>,
  mode: LivestreamMode,
  nextMode: LivestreamMode,
  modeSetting: {
    1v1: OneVsOneModeSetting | null,
    1vN: OneVsNModeSetting | null,
    free: FreeModeSetting | null,
    show: ShowModeSetting | null,
  },
  runningShowId: String | null,
  lastRunningShowId: String | null,
  show: {
    [showId]: PrivateShow,
  },
  privateShow: {
    status: {
      isFunding?: Boolean,
      isFundingFinalCall?: Boolean,
      isConsidering?: Boolean,
      isRefunding?: Boolean,
      isPreparing?: Boolean,
      isPerforming?: Boolean,
    },
  },
  sourceId: String,
  provider: String,
  providerInfo: {
    rtc?: {
      roomId: String,
    },
    rtm?: {
      appName: String,
      streamName: String,
    },
    trtc?: {
      roomId: String,
      userId: String,
      streamName: String,
      appName: String,
    }
    agora?: {}
  },
  runningInvitation: {
    1v1: Invitation | null,
    1vN: Invitation | null,
  },
  pendingCommands: Array<CommandItem>,
  pendingToyCommands: Array<ToyCommandItem>,
  runningCommand: CommandItem | null,
  runningToyCommand: ToyCommandItem | null,
  chat: {
    [id]: ChatMessage,
  },
  karaoke: {
    [id]: KaraokeItem,
  },
  rating: {
    [sessionId]: {
      rating?: Number,
      ratingCount?: Number,
    } | null,
  },
  isLiveStreaming: Boolean,
  viewers: Number,
  revenue: Number,
}

LivestreamMode: {
  name: String,
  nbf?: Number,
  exp?: Number,
  viewers?: Number,
  setting?: OneVsOneModeSetting | OneVsNModeSetting | FreeModeSetting | ShowModeSetting,
}

OneVsOneModeSetting: {
  price: Number,
  prepaidDurationMinutes: Number,
  initiator: String,
  invitationEnabled: Boolean,
}

OneVsNModeSetting: {
  price: Number,
  invitationEnabled: Boolean,
}

ShowModeSetting: {}

FreeModeSetting: {}

ShowBreakdown: {
  id: String,
  count: Number,
}

Invitation: {
  id: String,
  exp: Number,
  initiator: String,
  breakdowns: Array<InvitationBreakdown>,
  acceptedAt?: Number | null,
  declinedAt?: Number | null,
  progress?: Number,
}

InvitationBreakdown: {
  id: String,
}

ChatMessage: {
  id: String,
  type: LivestreamChatType,
  senderId: String,
  text: String,
  productId: String,
  showId: String,
  timestamp: Number,
  senderUsername: String,
  senderDisplayName: String,
  senderAvatarId: String,
  senderLevel: Number,
  senderClientId: String,
  senderBadges: Array<String>,
  tags: Array<String>,
  price: Number,
  count: Number,
  rating: Number,
  duration: Number,
}

KaraokeItem: {
  id: String,
  price: Number,
  duration: Number,
  device: {
    preset: String,
  } | null,
}

PrivateShow: {
  id: String,
  title: String,
  target: Number,
  progress: Number,
  exp: Number,
  startedAt?: Number,
  canceledAt?: Number,
  showedAt?: Number,
  completedAt?: Number,
  isFundingPrice: Boolean,
  metadata: {
    showTicketPrice: Number,
    fundingTicketPrice: Number,
    fundingNbf: Number,
    fundingExp: Number,
    fundingProgress: Number,
    fundingTarget: Number,
  },
  breakdowns: Array<ShowBreakdown>,
}

CommandItem: {
  id: String,
  initiator: String,
  metadata: {
    device: null,
    productId: String,
  },
  title?: String,
  progress?: Number,
  target?: Number,
  history?: Array<{type: 'start' | 'pause', timestamp: Number}>,
}

ToyCommandItem: {
  id: String,
  initiator: String,
  metadata: {
    device: {
      preset: String,
    },
    productId: String,
  },
  title?: String,
  progress?: Number,
  target?: Number,
  history?: Array<{type: 'start' | 'pause', timestamp: Number}>,
}
*/

/**
 * Livestream.
 * @module reducer/liveStream
 */
const liveStream = (state = defaultState, action) => {
  switch (action.type) {
    case MERGE_LIVE_STREAM_DATA:
      return _MERGE_LIVE_STREAM_DATA(action.payload)(state);
    case MERGE_LIVE_STREAMS_DATA:
      return _MERGE_LIVE_STREAMS_DATA(action.payload)(state);
    case REMOVE_LIVE_STREAM_DATA:
      return _REMOVE_LIVE_STREAM_DATA(action.payload)(state);
    case SET_LIVE_STREAM_DATA:
      return _SET_LIVE_STREAM_DATA(action.payload)(state);
    // -- PLOP_PREPEND_REDUCER_SWITCH_CASE --
    case LOGOUT:
      return _LOGOUT(action.payload)(state);
    default:
      return state;
  }
};

/**
 * Merge liveStream data
 * @kind reducer/actionType
 * @name MERGE_LIVE_STREAM_DATA
 * @param {array} {selectPath} - operation data select path
 * @param {any} {data} - operation data
 * @return {Immutable.Map} New state
 */
const _MERGE_LIVE_STREAM_DATA =
  ({ selectPath, data }) =>
  state => {
    return state.mergeDeepIn(selectPath, data);
  };

/**
 * Merge liveStreams' data
 * @kind reducer/actionType
 * @name MERGE_LIVE_STREAMS_DATA
 * @param {any} {data} - liveStream's data
 * @return {Immutable.Map} New state
 */
const _MERGE_LIVE_STREAMS_DATA =
  ({ data }) =>
  state =>
    state.mergeDeep(data);

/**
 * Remove live stream data
 * @kind reducer/actionType
 * @name REMOVE_LIVE_STREAM_DATA
 * @param {array} {selectPath} - select path
 * @return {Immutable.Map} New state
 */
const _REMOVE_LIVE_STREAM_DATA =
  ({ selectPath }) =>
  state => {
    return state.deleteIn(selectPath);
  };

/**
 * Set live stream data
 * @kind reducer/actionType
 * @name SET_LIVE_STREAM_DATA
 * @param {array} selectPath - been removed data select path
 * @param {any} data - operation data
 * @return {Immutable.Map} New state
 */
const _SET_LIVE_STREAM_DATA =
  ({ selectPath, data }) =>
  state => {
    return state.setIn([...selectPath], fromJS(data));
  };

// -- PLOP_PREPEND_REDUCER_ACTION_HANDLER --

/**
 * Logout
 * @kind reducer/actionType
 * @name LOGOUT
 * @return {Immutable.Map} New state
 */
const _LOGOUT = () => () => {
  return defaultState;
};

export default liveStream;
